import { useEffect, useState } from 'react'
import { toast } from 'react-toastify'
import { AxiosError } from 'axios'
import { AdminApi, AdminClassroom, AdminCourseGiftRule } from '../../services/adminApi'

type GridRow = {
  fullName: string
  email: string
  phone: string
  classroomId?: number
  fee?: string
}

type InvoiceMode = 'NOW' | 'LATER'

const emptyRow = (): GridRow => ({ fullName: '', email: '', phone: '' })
const formatNumberWithDot = (value: string) => {
  const digits = value.replace(/\D/g, '')
  if (!digits) return ''
  return Number(digits).toLocaleString('vi-VN')
}
const parseDotNumber = (value?: string) => {
  if (!value) return undefined
  const digits = value.replace(/\D/g, '')
  if (!digits) return undefined
  return Number(digits)
}
const reasonLabel: Record<string, string> = {
  duplicate_email_in_batch: 'Email bị trùng trong chính lô này',
  duplicate_phone_in_batch: 'Số điện thoại bị trùng trong chính lô này',
  invalid_classroom: 'Lớp học không hợp lệ',
  classroom_not_active: 'Lớp học đã dừng tuyển sinh',
  classroom_already_started: 'Lớp học đã bắt đầu',
  classroom_full: 'Lớp học đã đủ học viên',
  email_exists_phone_mismatch: 'Email đã tồn tại nhưng số điện thoại không khớp',
  phone_exists_in_system: 'Số điện thoại đã tồn tại trong hệ thống',
  registration_already_completed: 'Tài khoản đã hoàn tất đăng ký lớp này',
  registration_status_blocks_new: 'Trạng thái đăng ký hiện tại không cho phép tạo mới'
}

type BatchValidationError = {
  rowIndex: number
  email: string
  phone: string
  classroomId: number
  reasons: string[]
  reasonMessages?: string[]
  account?: { email: string; phone?: string }
}
type BatchErrorPopupItem = {
  fullName: string
  email: string
  phone: string
  reasonText: string
}

const AdminEnterpriseRegistrationPage = () => {
  const [taxCode, setTaxCode] = useState('')
  const [enterpriseName, setEnterpriseName] = useState('')
  const [invoiceEmail, setInvoiceEmail] = useState('')
  const [address, setAddress] = useState('')
  /** NOW = xuất HĐ ngay; LATER = xuất khi admin vào danh sách và bấm Xuất hóa đơn */
  const [invoiceMode, setInvoiceMode] = useState<InvoiceMode>('LATER')
  const [rows, setRows] = useState<GridRow[]>([emptyRow()])
  const [classrooms, setClassrooms] = useState<AdminClassroom[]>([])
  /** Cache KM tặng khóa B theo course_id (mua A → tặng B) */
  const [giftRulesByCourseId, setGiftRulesByCourseId] = useState<Record<number, AdminCourseGiftRule[]>>({})
  const [saving, setSaving] = useState(false)
  const [batchErrors, setBatchErrors] = useState<BatchValidationError[]>([])
  const [errorPopupMessage, setErrorPopupMessage] = useState('')
  const [errorPopupItems, setErrorPopupItems] = useState<BatchErrorPopupItem[]>([])
  const errorRowIndexes = (() => {
    const indexes = new Set<number>()
    batchErrors.forEach((e) => {
      if (Number.isInteger(e.rowIndex) && e.rowIndex >= 0 && e.rowIndex < rows.length) {
        indexes.add(e.rowIndex)
      }
      const idxByIdentity = rows.findIndex((r) => {
        const rowEmail = r.email.trim().toLowerCase()
        const rowPhone = r.phone.trim()
        const errEmail = (e.account?.email || e.email || '').trim().toLowerCase()
        const errPhone = (e.account?.phone || e.phone || '').trim()
        if (!errEmail) return false
        return rowEmail === errEmail && (!errPhone || !rowPhone || rowPhone === errPhone)
      })
      if (idxByIdentity >= 0) {
        indexes.add(idxByIdentity)
      }
    })
    return indexes
  })()

  useEffect(() => {
    ;(async () => {
      try {
        const res = await AdminApi.getClassrooms()
        setClassrooms(res.data.items)
      } catch {
        toast.error('Không tải được danh sách lớp')
      }
    })()
  }, [])

  const ensureGiftRulesForCourse = async (courseId: number) => {
    if (!courseId || giftRulesByCourseId[courseId] !== undefined) return
    try {
      const res = await AdminApi.getCourseGiftRules(courseId)
      const active = (res.data?.items || []).filter((r) => r.isActive !== false)
      setGiftRulesByCourseId((prev) => ({ ...prev, [courseId]: active }))
    } catch {
      setGiftRulesByCourseId((prev) => ({ ...prev, [courseId]: [] }))
    }
  }

  const giftLabelForClassroom = (classroomId?: number): string | null => {
    if (!classroomId) return null
    const classroom = classrooms.find((c) => c.id === classroomId)
    if (!classroom) return null
    const rules = giftRulesByCourseId[classroom.course_id]
    if (!rules || rules.length === 0) return null
    const names = rules
      .map((r) => r.giftCourseName || r.giftCourseCode || `Khóa #${r.giftCourseId}`)
      .filter(Boolean)
    if (names.length === 0) return null
    return `KM: đăng ký ${classroom.course_code || classroom.course_name} → tặng ${names.join(', ')}`
  }

  const updateRow = (index: number, patch: Partial<GridRow>) => {
    setRows((prev) => prev.map((r, i) => (i === index ? { ...r, ...patch } : r)))
  }

  const addRow = () => setRows((prev) => [...prev, emptyRow()])
  const removeRow = (index: number) => setRows((prev) => (prev.length <= 1 ? prev : prev.filter((_, i) => i !== index)))
  const showBatchErrorPopup = (errors: BatchValidationError[], fallbackMessage?: string) => {
    try {
      const items = errors.map((err) => {
        const idxByIdentity = rows.findIndex((r) => {
          const rowEmail = r.email.trim().toLowerCase()
          const rowPhone = r.phone.trim()
          const errEmail = (err.account?.email || err.email || '').trim().toLowerCase()
          const errPhone = (err.account?.phone || err.phone || '').trim()
          if (!errEmail) return false
          return rowEmail === errEmail && (!errPhone || !rowPhone || rowPhone === errPhone)
        })
        const resolvedIndex = idxByIdentity >= 0 ? idxByIdentity : err.rowIndex
        const row = rows[resolvedIndex]
        const fullName = row?.fullName?.trim() || '(chưa có họ tên)'
        const email = (err.account?.email || err.email || row?.email || '').trim() || '(trống)'
        const phone = (err.account?.phone || err.phone || row?.phone || '').trim() || '(trống)'
        const reasonText = (err.reasonMessages && err.reasonMessages.length > 0
          ? err.reasonMessages
          : (err.reasons || []).map((reason) => reasonLabel[reason] || reason)
        ).join('; ')
        return { fullName, email, phone, reasonText: reasonText || 'Dữ liệu không hợp lệ' }
      })
      setErrorPopupMessage(fallbackMessage || 'Lô đăng ký có dữ liệu không hợp lệ')
      setErrorPopupItems(items)
      const alertLines = items.map((item) => `- ${item.fullName} | ${item.email} | ${item.phone} | ${item.reasonText}`)
      window.alert(`${fallbackMessage || 'Lô đăng ký có dữ liệu không hợp lệ'}\n\n${alertLines.join('\n')}`)
    } catch {
      window.alert(fallbackMessage || 'Lô đăng ký có dữ liệu không hợp lệ')
    }
  }

  const submit = async () => {
    if (!taxCode || !enterpriseName || !invoiceEmail || !address) {
      toast.error('Vui lòng nhập đủ thông tin doanh nghiệp')
      return
    }
    const cleanedRows = rows
      .map((r, sourceRowIndex) => ({
        fullName: r.fullName.trim(),
        email: r.email.trim(),
        phone: r.phone.trim(),
        classroomId: r.classroomId as number,
        fee: parseDotNumber(r.fee),
        sourceRowIndex
      }))
      .filter((r) => r.fullName && r.email && r.classroomId)
    if (cleanedRows.some((r) => r.fee != null && (!Number.isFinite(r.fee) || r.fee < 0))) {
      toast.error('Giá không hợp lệ')
      return
    }
    if (cleanedRows.length === 0) {
      toast.error('Cần tối thiểu 1 dòng nhân viên hợp lệ')
      return
    }

    setSaving(true)
    setBatchErrors([])
    try {
      const response = (await AdminApi.createEnterpriseBatch({
        taxCode,
        enterpriseName,
        invoiceEmail,
        address,
        invoiceMode,
        rows: cleanedRows
      })) as {
        success?: boolean
        message?: string
        data?: {
          rowErrors?: BatchValidationError[]
          invoiceMode?: InvoiceMode
          postProcessSummary?: Array<{ giftedCourseNames?: string[] }>
        }
      }

      if (response?.success === false) {
        const apiErrors = response?.data?.rowErrors || []
        if (apiErrors.length > 0) {
          setBatchErrors(apiErrors)
          showBatchErrorPopup(apiErrors, response?.message)
        } else {
          toast.error(response?.message || 'Có dòng dữ liệu không hợp lệ, vui lòng kiểm tra lại')
        }
        return
      }

      const gifted = Array.from(
        new Set(
          (response?.data?.postProcessSummary || [])
            .flatMap((s) => s.giftedCourseNames || [])
            .filter(Boolean)
        )
      )
      const giftMsg = gifted.length > 0 ? ` Đã tặng KM: ${gifted.join(', ')}.` : ''
      const invoiceMsg =
        invoiceMode === 'LATER'
          ? ' Chưa xuất HĐ — xuất khi vào Danh sách đăng ký → Xuất hóa đơn.'
          : ' Đã gửi yêu cầu xuất hóa đơn.'
      toast.success((response?.message || 'Đã tạo lô đăng ký DN') + giftMsg + invoiceMsg)
      setRows([emptyRow()])
    } catch (error) {
      const axiosError = error as AxiosError<{
        message?: string
        data?: { rowErrors?: BatchValidationError[]; accountList?: string[] }
        rowErrors?: BatchValidationError[]
      }>
      const payload = axiosError.response?.data
      const apiMessage = payload?.message
      const apiErrors = payload?.data?.rowErrors ?? payload?.rowErrors
      if (apiMessage) {
        // Immediate native popup in catch branch to guarantee visibility.
        window.alert(apiMessage)
      }

      if (apiErrors && apiErrors.length > 0) {
        setBatchErrors(apiErrors)
        showBatchErrorPopup(apiErrors, apiMessage)
      } else {
        // Fallback: parse account list from message and map back to current row indexes.
        const fallbackErrors: BatchValidationError[] = []
        const accountChunk = (apiMessage || '').split('Account lỗi:')[1]?.trim()
        if (accountChunk) {
          const accountEntries = accountChunk
            .split(',')
            .map((s) => s.trim())
            .filter(Boolean)

          accountEntries.forEach((entry) => {
            const m = entry.match(/^(.+?)(?:\s*\((.*?)\))?$/)
            const email = (m?.[1] || '').trim().toLowerCase()
            const phone = (m?.[2] || '').trim()
            const rowIndex = rows.findIndex((r) => {
              const rowEmail = r.email.trim().toLowerCase()
              const rowPhone = r.phone.trim()
              return rowEmail === email && (!phone || !rowPhone || rowPhone === phone)
            })
            if (rowIndex >= 0) {
              fallbackErrors.push({
                rowIndex,
                email,
                phone,
                classroomId: Number(rows[rowIndex]?.classroomId || 0),
                reasons: ['batch_validation_failed']
              })
            }
          })
        }

        if (fallbackErrors.length > 0) {
          setBatchErrors(fallbackErrors)
          showBatchErrorPopup(fallbackErrors, apiMessage)
        } else {
          setBatchErrors([])
          toast.error(apiMessage || 'Không thể lưu lô đăng ký')
        }
      }
    } finally {
      setSaving(false)
    }
  }

  return (
    <div className="space-y-4">
      <h1 className="text-xl font-semibold text-m2-on-surface">Đăng ký khóa học cho DN</h1>

      {errorPopupItems.length > 0 && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
          <div className="w-full max-w-3xl rounded-lg bg-m2-surface-container p-4 shadow-xl">
            <div className="mb-3 flex items-center justify-between">
              <h2 className="text-base font-semibold text-red-700">Lỗi đăng ký theo DN</h2>
              <button
                className="rounded border border-m2-outline px-2 py-1 text-sm"
                onClick={() => {
                  setErrorPopupItems([])
                  setErrorPopupMessage('')
                }}
              >
                Đóng
              </button>
            </div>
            <p className="mb-3 text-sm text-m2-on-surface">{errorPopupMessage}</p>
            <div className="max-h-[55vh] overflow-auto rounded border border-m2-outline-variant">
              <table className="min-w-full text-sm">
                <thead className="bg-m2-surface-low text-left text-m2-on-surface">
                  <tr>
                    <th className="px-3 py-2">Họ tên</th>
                    <th className="px-3 py-2">Email</th>
                    <th className="px-3 py-2">SĐT</th>
                    <th className="px-3 py-2">Lỗi</th>
                  </tr>
                </thead>
                <tbody>
                  {errorPopupItems.map((item, idx) => (
                    <tr key={`${item.email}-${idx}`} className="border-t border-m2-outline-variant align-top">
                      <td className="px-3 py-2">{item.fullName}</td>
                      <td className="px-3 py-2">{item.email}</td>
                      <td className="px-3 py-2">{item.phone}</td>
                      <td className="px-3 py-2 text-red-700">{item.reasonText}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {batchErrors.length > 0 && (
        <div className="rounded-lg border border-m2-error/30 bg-m2-error/10 p-3">
          <h2 className="mb-2 text-sm font-semibold text-red-700">Lỗi dữ liệu lô đăng ký</h2>
          <div className="mb-2 text-sm text-red-700">
            Account lỗi:{' '}
            {batchErrors
              .map((e) => `${e.email}${e.phone ? ` (${e.phone})` : ''}`)
              .join(', ')}
          </div>
          <ul className="space-y-2 text-sm text-red-700">
            {batchErrors.map((err, i) => (
              <li key={`${err.rowIndex}-${i}`} className="rounded border border-red-100 bg-m2-surface-container p-2">
                <div>
                  Dòng {err.rowIndex + 1} - Email: {err.email} - SĐT: {err.phone || '(trống)'}
                </div>
                <div className="mt-1">
                  {(err.reasonMessages && err.reasonMessages.length > 0
                    ? err.reasonMessages
                    : err.reasons.map((reason) => reasonLabel[reason] || reason)
                  ).join('; ')}
                </div>
              </li>
            ))}
          </ul>
        </div>
      )}

      <div className="rounded-lg border border-m2-outline-variant bg-m2-surface-container p-4">
        <h2 className="mb-3 text-sm font-semibold text-m2-on-surface">Thông tin doanh nghiệp / xuất hóa đơn</h2>
        <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
          <input className="rounded border border-m2-outline px-3 py-2 text-sm" placeholder="Mã số thuế" value={taxCode} onChange={(e) => setTaxCode(e.target.value)} />
          <input className="rounded border border-m2-outline px-3 py-2 text-sm" placeholder="Tên doanh nghiệp" value={enterpriseName} onChange={(e) => setEnterpriseName(e.target.value)} />
          <input className="rounded border border-m2-outline px-3 py-2 text-sm" placeholder="Email nhận hóa đơn" value={invoiceEmail} onChange={(e) => setInvoiceEmail(e.target.value)} />
          <input className="rounded border border-m2-outline px-3 py-2 text-sm" placeholder="Địa chỉ" value={address} onChange={(e) => setAddress(e.target.value)} />
        </div>

        <div className="mt-4 rounded border border-m2-outline-variant bg-m2-surface-low/60 p-3">
          <p className="mb-2 text-sm font-medium text-m2-on-surface">Thời điểm xuất hóa đơn</p>
          <div className="flex flex-col gap-2 sm:flex-row sm:gap-6">
            <label className="inline-flex cursor-pointer items-start gap-2 text-sm text-m2-on-surface">
              <input
                type="radio"
                name="invoiceMode"
                className="mt-0.5"
                checked={invoiceMode === 'NOW'}
                onChange={() => setInvoiceMode('NOW')}
              />
              <span>
                <span className="font-medium">Xuất ngay</span>
                <span className="block text-xs text-m2-on-surface-variant">Gọi MISA phát hành HĐ ngay sau khi lưu lô</span>
              </span>
            </label>
            <label className="inline-flex cursor-pointer items-start gap-2 text-sm text-m2-on-surface">
              <input
                type="radio"
                name="invoiceMode"
                className="mt-0.5"
                checked={invoiceMode === 'LATER'}
                onChange={() => setInvoiceMode('LATER')}
              />
              <span>
                <span className="font-medium">Xuất sau</span>
                <span className="block text-xs text-m2-on-surface-variant">
                  Chỉ lưu đăng ký; xuất HĐ khi vào Danh sách đăng ký và bấm &quot;Xuất hóa đơn&quot;
                </span>
              </span>
            </label>
          </div>
        </div>
      </div>

      <div className="rounded-lg border border-m2-outline-variant bg-m2-surface-container p-4">
        <div className="mb-3 flex items-center justify-between">
          <div>
            <h2 className="text-sm font-semibold text-m2-on-surface">Lưới nhân viên</h2>
            <p className="mt-0.5 text-xs text-m2-on-surface-variant">
              Đăng ký khóa A: hệ thống tự tặng khóa B theo KM (nếu có cấu hình). Học viên chọn lớp khóa tặng sau.
            </p>
          </div>
          <button className="rounded border border-m2-outline px-3 py-1 text-sm" onClick={addRow}>
            + Thêm dòng
          </button>
        </div>
        <div className="space-y-2">
          {rows.map((row, idx) => {
            const giftHint = giftLabelForClassroom(row.classroomId)
            return (
              <div
                key={idx}
                className={`rounded border p-3 ${
                  errorRowIndexes.has(idx) ? 'border-red-300 bg-m2-error/10/40' : 'border-m2-outline-variant'
                }`}
              >
                <div className="grid grid-cols-1 gap-2 md:grid-cols-[2fr_2fr_1.3fr_2fr_1.2fr_auto]">
                  <input className="rounded border border-m2-outline px-3 py-2 text-sm" placeholder="Họ tên" value={row.fullName} onChange={(e) => updateRow(idx, { fullName: e.target.value })} />
                  <input className="rounded border border-m2-outline px-3 py-2 text-sm" placeholder="Email" value={row.email} onChange={(e) => updateRow(idx, { email: e.target.value })} />
                  <input className="rounded border border-m2-outline px-3 py-2 text-sm" placeholder="Số điện thoại" value={row.phone} onChange={(e) => updateRow(idx, { phone: e.target.value })} />
                  <select
                    className="w-full rounded border border-m2-outline px-3 py-2 text-sm md:max-w-[240px]"
                    value={row.classroomId ?? ''}
                    onChange={(e) => {
                      const classroomId = e.target.value ? Number(e.target.value) : undefined
                      const selectedClassroom = classrooms.find((c) => c.id === classroomId)
                      updateRow(idx, {
                        classroomId,
                        fee: selectedClassroom ? formatNumberWithDot(String(Number(selectedClassroom.fee || 0))) : ''
                      })
                      if (selectedClassroom?.course_id) {
                        void ensureGiftRulesForCourse(selectedClassroom.course_id)
                      }
                    }}
                  >
                    <option value="">Chọn lớp</option>
                    {classrooms.map((c) => (
                      <option key={c.id} value={c.id}>
                        {c.course_code} - {c.code} - {c.name}
                      </option>
                    ))}
                  </select>
                  <input
                    className="rounded border border-m2-outline px-3 py-2 text-sm"
                    placeholder="Giá"
                    inputMode="numeric"
                    value={row.fee ?? ''}
                    onChange={(e) => updateRow(idx, { fee: formatNumberWithDot(e.target.value) })}
                  />
                  <button className="rounded border border-red-300 px-2 py-2 text-sm text-red-700" onClick={() => removeRow(idx)}>
                    Xóa
                  </button>
                </div>
                {giftHint && (
                  <p className="mt-2 text-xs font-medium text-emerald-700">{giftHint}</p>
                )}
              </div>
            )
          })}
        </div>
      </div>

      <div className="flex justify-end">
        <button className="rounded bg-m2-primary px-4 py-2 text-sm font-medium text-white disabled:opacity-60" disabled={saving} onClick={submit}>
          {saving ? 'Đang lưu...' : 'Lưu lô đăng ký'}
        </button>
      </div>
    </div>
  )
}

export default AdminEnterpriseRegistrationPage
